import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const SVG_HEIGHT = 400;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '10px 14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '240px',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '16px',
  },
  legendBar: {
    width: '200px',
    height: '10px',
    borderRadius: '5px',
  },
  legendLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '200px',
    marginTop: '4px',
    fontSize: '10px',
    color: '#6b7280',
  },
};

function shouldUseWhiteText(hexColor) {
  const r = parseInt(hexColor.slice(1, 3), 16) / 255;
  const g = parseInt(hexColor.slice(3, 5), 16) / 255;
  const b = parseInt(hexColor.slice(5, 7), 16) / 255;
  const toLinear = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance < 0.45;
}

function getChangeColor(changePercent) {
  const val = parseFloat(changePercent || 0);
  if (Math.abs(val) < 0.001) return '#9ca3af'; // Gray for zero

  if (val < 0) {
    return '#16a34a'; // Force Green for ANY decrease
  } else {
    return '#dc2626'; // Force Red for ANY increase
  }
}

export default function TreemapPricing({ data }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.addresses || data.addresses.length === 0) return;
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.getBoundingClientRect().width;
    if (containerWidth <= 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', containerWidth).attr('height', SVG_HEIGHT);

    const stateGroups = {};
    data.addresses.forEach((addr) => {
      const state = addr.state || 'Unknown';
      if (!stateGroups[state]) stateGroups[state] = [];
      stateGroups[state].push(addr);
    });

    const hierarchyData = {
      name: 'root',
      children: Object.entries(stateGroups).map(([state, addresses]) => ({
        name: state,
        children: addresses.map((addr) => ({
          name: addr.label,
          addressId: addr.addressId,
          state: addr.state,
          price: addr.price,
          changePercent: parseFloat(addr.changePercent || 0),
        })),
      })),
    };

    const root = d3.hierarchy(hierarchyData)
      .sum((d) => d.price || 0)
      .sort((a, b) => b.value - a.value);

    const treemap = d3.treemap()
      .size([containerWidth, SVG_HEIGHT])
      .paddingOuter(3)
      .paddingInner(1)
      .paddingTop(16)
      .tile(d3.treemapSquarify);

    treemap(root);

    svg.selectAll('.state-group')
      .data(root.children || [])
      .enter()
      .append('rect')
      .attr('class', 'state-group')
      .attr('x', (d) => d.x0)
      .attr('y', (d) => d.y0)
      .attr('width', (d) => d.x1 - d.x0)
      .attr('height', (d) => d.y1 - d.y0)
      .attr('fill', 'none')
      .attr('stroke', '#374151')
      .attr('stroke-width', 2)
      .attr('rx', 2)
      .attr('ry', 2);

    svg.selectAll('.state-label')
      .data(root.children || [])
      .enter()
      .append('text')
      .attr('class', 'state-label')
      .attr('x', (d) => d.x0 + 4)
      .attr('y', (d) => d.y0 + 12)
      .attr('font-size', '10px')
      .attr('font-weight', '700')
      .attr('fill', '#374151')
      .text((d) => d.data.name);

    const defs = svg.append('defs');
    const leaves = root.leaves();

    leaves.forEach((leaf, i) => {
      const w = leaf.x1 - leaf.x0;
      const h = leaf.y1 - leaf.y0;
      defs.append('clipPath')
        .attr('id', 'treemap-clip-' + i)
        .append('rect')
        .attr('width', Math.max(0, w))
        .attr('height', Math.max(0, h));
    });

    const cells = svg.selectAll('.cell')
      .data(leaves)
      .enter()
      .append('g')
      .attr('class', 'cell')
      .attr('transform', (d) => 'translate(' + d.x0 + ',' + d.y0 + ')');

    cells.each(function (d, i) {
      const g = d3.select(this);
      const w = d.x1 - d.x0;
      const h = d.y1 - d.y0;
      const fillColor = getChangeColor(d.data.changePercent);
      const textColor = shouldUseWhiteText(fillColor) ? '#ffffff' : '#1f2937';

      g.append('rect')
        .attr('width', Math.max(0, w))
        .attr('height', Math.max(0, h))
        .attr('fill', fillColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1);

      if (w > 50 && h > 30) {
        const labelGroup = g.append('g').attr('clip-path', 'url(#treemap-clip-' + i + ')');
        labelGroup.append('text')
          .attr('x', w / 2)
          .attr('y', h / 2 - 4)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', '600')
          .attr('fill', textColor)
          .text(d.data.name);

        if (h > 44) {
          labelGroup.append('text')
            .attr('x', w / 2)
            .attr('y', h / 2 + 10)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', '9px')
            .attr('fill', textColor)
            .attr('opacity', 0.9)
            .text('$' + (d.data.price || 0).toFixed(2));
        }
      }
    });

    cells.on('mousemove', function (event, d) {
        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          label: d.data.name,
          state: d.data.state,
          price: d.data.price,
          changePercent: d.data.changePercent,
        });
      })
      .on('mouseleave', function () { setTooltip(null); });

    return () => { svg.selectAll('*').remove(); };
  }, [data]);

  if (!data || !data.addresses || data.addresses.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Pricing Portfolio Map</div>
        <div style={styles.emptyState}>No pricing portfolio data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Pricing Portfolio Map</div>
      <div style={styles.subtitle}>Each rectangle sized by price, colored by change %</div>
      <div ref={containerRef} style={{ width: '100%' }}><svg ref={svgRef} /></div>
      <div style={styles.legendContainer}>
        <div style={{ ...styles.legendBar, background: 'linear-gradient(to right, #16a34a, #9ca3af, #dc2626)' }} />
        <div style={styles.legendLabels}><span>Decrease</span><span>Increase</span></div>
      </div>
      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{tooltip.label}</div>
          <div>Change: <span style={{ color: getChangeColor(tooltip.changePercent), fontWeight: 700 }}>{parseFloat(tooltip.changePercent || 0).toFixed(2)}%</span></div>
          <div>Current: <strong>${(tooltip.price || 0).toFixed(2)}</strong></div>
        </div>
      )}
    </div>
  );
}